import { useEffect, useState } from 'react';

import {
  AIUnitUsageByPodRequest,
  AIUnitWorkspaceUsageRequest,
  AvailableWorkspacesRequest,
  Granularity,
  PodUsageRecord,
  WorkspaceSummary,
} from '../../../../admin-center/gen/aiUnitWorkspace/aiUnitWorkspace_pb';
import { useAdminCenterService } from '../../../../admin-center/hooks';
import { useUser } from '../../../../utils/hooks';
import { FlatRawChartData } from '../../common/types';
import { buildUserContext, formatDateLocal, transformFlatDataForCharts } from '../../common/utils';
import { AIUnitWorkspace } from './AIUnitWorkspacePage.utils';

export const useAIUnitWorkspaceHook = (startDate: Date, endDate: Date, workspaces: string[]) => {
  const service = useAdminCenterService();
  const [events, setEvents] = useState<AIUnitWorkspace[] | null>(null);
  const [workspaceTotals, setWorkspaceTotals] = useState<WorkspaceSummary[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const user = useUser();

  useEffect(() => {
    setEventsLoading(true);
    const fetchWorkspaceData = async () => {
      try {
        const userContext = await buildUserContext(user);
        const request: AIUnitWorkspaceUsageRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
          workspaces: workspaces,
          userContext: userContext,
        };
        const response = await service.getAiUnitWorkspaceUsageRequest(request);

        const transformedEvents: AIUnitWorkspace[] =
          response.podUsageRecords?.map((event: PodUsageRecord) => {
            return {
              workspace: event.workspaceName ?? 'unknown',
              namespace: event.namespace ?? 'unknown',
              pod: event.podName ?? 'unknown',
              username: event.userName ?? 'unknown',
              aiunit: event.aiUnit ?? 0.0,
            };
          }) ?? [];

        setEvents(transformedEvents);
        setWorkspaceTotals(response.workspaceSummaries || []);
      } catch (error) {
        setError('Error fetching workspace level ai unit');
      } finally {
        setEventsLoading(false);
      }
    };

    fetchWorkspaceData();
  }, [startDate, endDate, workspaces, service, user]);

  return { events, workspaceTotals, eventsLoading, error };
};

export const useAvailableWorkspaces = (startDate: Date, endDate: Date) => {
  const service = useAdminCenterService();
  const [availableWorkspaces, setAvailableWorkspaces] = useState<string[]>([]);
  const [availableWorkspacesLoading, setAvailableWorkspacesLoading] = useState<boolean>(true);
  const [availableWorkspacesError, setAvailableWorkspacesError] = useState<string | null>(null);

  useEffect(() => {
    setAvailableWorkspacesLoading(true);
    const fetchAvailableWorkspaces = async () => {
      try {
        const request: AvailableWorkspacesRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
        };
        const response = await service.getAvailableWorkspacesRequest(request);
        setAvailableWorkspaces(response.availableWorkspaces || []);
      } catch (error) {
        setAvailableWorkspacesError('Error fetching workspace level ai unit');
      } finally {
        setAvailableWorkspacesLoading(false);
      }
    };

    fetchAvailableWorkspaces();
  }, [startDate, endDate, service]);

  return { availableWorkspaces, availableWorkspacesLoading, availableWorkspacesError };
};

export const useAiUnitUsageByPod = (
  startDate: Date,
  endDate: Date,
  username: string,
  podName: string,
  namespace: string,
  workspace: string,
  granularity: 'monthly' | 'weekly'
) => {
  const service = useAdminCenterService();
  const [podUsageData, setPodUsageData] = useState<any>(null);
  const [podUsageDataLoading, setPodUsageDataLoading] = useState<boolean>(true);
  const [podUsageDataError, setPodUsageDataError] = useState<string | null>(null);

  useEffect(() => {
    setPodUsageDataLoading(true);
    const fetchUsageByPod = async () => {
      try {
        const request: AIUnitUsageByPodRequest = {
          startDate: formatDateLocal(startDate),
          endDate: formatDateLocal(endDate),
          username: username,
          podName: podName,
          namespace: namespace,
          workspace: workspace,
          granularity: granularity === 'monthly' ? Granularity.MONTHLY : Granularity.WEEKLY,
        };
        const response = await service.getAiUnitUsageByPodRequest(request);
        const transformedData = transformFlatDataForCharts(response.data as FlatRawChartData, 'AI Unit Usage');
        setPodUsageData(transformedData);
      } catch (error) {
        setPodUsageDataError('Error fetching workspace level ai unit');
      } finally {
        setPodUsageDataLoading(false);
      }
    };

    fetchUsageByPod();
  }, [startDate, endDate, username, podName, namespace, workspace, granularity, service]);

  return { podUsageData, podUsageDataLoading, podUsageDataError };
};
